import { Box, Button, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form';
import { RHFAutocomplete } from '../form';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useCustom, useCustomMutation, useNotification, useUpdate } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';
import { IContact } from '@/utils/zod/contactFormValidation';

interface NoOutstandingModalProps { 
    visible: boolean, 
    close: () => void, 
    data: { 
        ledger_name: string, 
        members: { 
            id: string, 
            name: string, 
            phone_number: string 
        }[] 
    }[], 
    senderAccountId: string, 
    onSubmit: () => void 
}

export const NoOutstandingModal = ({ visible, close, data, senderAccountId, onSubmit }: NoOutstandingModalProps ) => {

    const [isSubmitting, setIsSubmitting] = useState(false);
    const { open } = useNotification();

    const { mutateAsync: updateMutate } = useUpdate({
        resource: "companies",
    });
    const { data: agents, isLoading: isAgentsLoading } = useCustom<IContact[]>({
        url: '/accounts/companies/agent',
        method: 'get'
    })
    const {mutateAsync: sendOutstanding } = useCustomMutation()

    const formValues = useForm()

    const handleSendOutstanding = formValues.handleSubmit(async (data) => {
        setIsSubmitting(true);
        try {
            const updateCompanyPromises = Object.keys(data).map(async(key: string) => updateMutate({
                id: key.split('_')[key.split('_').length - 1],
                values: {
                    send_outstanding_to_accounts: data[key].map((account_id: string) => ({ id: account_id })),
                },
            }));
            const response = await Promise.all(updateCompanyPromises);
            await sendOutstanding({
                url:'/companies/send-outstanding-to-accounts',
                method: 'post',
                values: {
                    sender_account_id: senderAccountId,
                    companies: response.map((res) => res.data.ledger_name),
                }
            }, {
                onSuccess: (response) => {
                  response.data?.forEach((response: any) => {
                    if (response.status === "error") {
                      open?.({
                        message: typeof response.message === "string" ? response.message : response.message.error,
                        description: `Error sending outstanding for ${response.ledger_name}`,
                        type: "error",
                      })
                    } else {
                      open?.({
                        message: response.message,
                        description: `Successfully sent outstanding for ${response.ledger_name}`,
                        type: "success",
                      })
                    }
                  })
                },
                onError: (errors) => {
                  if (Array.isArray(errors.response.data)) {
                    errors.response.data.forEach((error: any) => {
                      open?.({
                        message: typeof error.message === 'string' ? error.message : error.message.error,
                        description: `Error sending outstanding for ${error.ledger_name}`,
                        type: "error",
                      })
                    })
                  } else {
                    open?.({
                      message: "Error sending outstanding",
                      description: errors.message,
                      type: "error",
                    })
                  }
                },
              })

        } catch (error: any) {
            console.log(error);
            open?.({
                type: "error",
                message: typeof error === "string" ? error : error.response.data.error,
            });
        } finally {
            setIsSubmitting(false);
            formValues.reset();
            onSubmit()
            close();
        }
    })

    const columns = useMemo<MRT_ColumnDef<{ ledger_name: string, members: { id: string, name: string, phone_number: string }[] }>[]>(
        () => [
            //ledger_name
            {
                accessorKey: 'ledger_name',
                header: 'Ledger Name',
                Cell: ({ row }) => <Typography>{row.original.ledger_name}</Typography>,
                size: 300,
            },

            //send_outstanding_to_accounts
            {
                header: 'Send Outstanding To',
                size: 300,
                enableSorting: false,
                Cell: ({ row }) => {
                    if (isAgentsLoading) {
                        return <CircularProgress size={20} />;
                    }

                    return (
                        <RHFAutocomplete
                            name={`send_outstanding_to_accounts_${row.original.ledger_name}`}
                            options={[{
                                title: "Agents",
                                options: (agents?.data || []).map((agent: any) => ({
                                    label: `${agent.name ? `${(agent.name)}: ` : ""}${agent.user?.phone_number || ""}`,
                                    value: agent.id,
                                })),
                            },{
                                title: "Members",
                                options: row.original.members.map((member: any) => ({
                                    label: `${member.name ? `${(member.name)}: ` : ""}${member.phone_number || ""}`,
                                    value: member.id,
                                })),
                            }]}
                            multiple={true}
                            createNew={false}
                            label="Select Members"
                            size='small'
                            required={true}
                            grouped={true}
                        />
                    );
                }
            },
        ],
        [agents, isAgentsLoading]
    );

    return (
        <Modal open={visible} onClose={isSubmitting ? undefined :close}>
            <FormProvider {...formValues}>
                <Box
                    component="form"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 1000,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        maxHeight: '200vh',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        No Outstanding Accounts found for the following companies.
                    </Typography>
                    <MaterialReactTable
                        columns={columns}
                        data={data}
                        enablePagination
                        muiTableBodyRowProps={{ hover: true }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 20, 40, 80],
                            showFirstButton: true,
                            showLastButton: true
                        }}
                        state={{
                            showGlobalFilter: true,
                        }}
                        getRowId={(row) => row.ledger_name}
                        initialState={{
                            density: 'compact',
                            pagination: { pageSize: 10, pageIndex: 0 },
                            sorting: [{ id: 'ledger_name', desc: true }],
                        }}
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => { formValues.reset(); close(); }} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendOutstanding}
                            disabled={isSubmitting || Object.keys(formValues.formState.dirtyFields).length === 0}
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                        {isSubmitting ? "Sending..." : "Save & Send"}
                        </Button>
                    </Stack>
                </Box>
            </FormProvider>
        </Modal>
    )
}